/**
 * GameSessionContext — lets anything rendered inside a game ask for a brand-new
 * question set without the game itself having to know how.
 *
 * `newSet()` bumps a nonce in App.jsx that is part of the game's React `key`,
 * so the whole game component remounts and re-rolls its pool. That works no
 * matter how a given game builds its questions (a `useState(() => …)`
 * initializer, a module-level const, a `useEffect`, or a server fetch) — which
 * is why the reset button can exist without touching 90 game files.
 *
 * GameBingo has done this locally for a while ("key remount = real replay");
 * this is the same trick, lifted to the router so every game gets it.
 *
 * `newSet` is null outside a game, so consumers render nothing.
 *
 * `guest` travels the same way and for the same reason. A game mounted on a
 * public /games/:id page is played by someone with no account, so WinScreen has
 * to bank the result locally and ask them to sign up instead of posting it —
 * and WinScreen is rendered by each game, not by the page. Passing it through
 * the context keeps all 90 games untouched.
 *
 * `gameId` + `source` ride along for analytics: this provider wraps every game
 * mount (app, journey, teacher preview, public page), so game_start fires here
 * once per game and WinScreen reads the id for game_complete — its own
 * `gameName` prop is a Hebrew display string, not a stable id.
 */
import { createContext, useContext, useEffect, useMemo } from "react";
import { ALL_GAMES } from "../data/games";
import { track } from "../utils/analytics";

const GameSessionContext = createContext({ newSet: null, guest: false, onSignup: null, gameId: null, source: null });

export const useGameSession = () => useContext(GameSessionContext);

const subjectOf = (id) => ALL_GAMES.find(g => g.id === id)?.subject;

export function GameSessionProvider({ newSet, guest = false, onSignup = null, gameId = null, source = null, children }) {
  useEffect(() => {
    if (gameId) track("game_start", { game_id: gameId, subject: subjectOf(gameId), source });
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps -- once per game, not per source change

  const value = useMemo(() => ({
    newSet: newSet && (() => { track("game_new_set", { game_id: gameId, source }); newSet(); }),
    guest,
    onSignup: onSignup && (() => { track("guest_signup_click", { game_id: gameId }); onSignup(); }),
    gameId,
    source,
  }), [newSet, guest, onSignup, gameId, source]);
  return (
    <GameSessionContext.Provider value={value}>
      {children}
    </GameSessionContext.Provider>
  );
}
